import { useRemoteResource } from "./useRemoteResource";
import { useScoreApi, unwrap } from "../api/scoreApi";

// #642 — the whole ring's rides for the show, in ONE call (#637 added the
// showId/day/ring filter to GET /ride, backed by the existing ridesByShow GSI).
// Before that this would have been one request per class — 40 round trips over
// venue wifi on every refresh.
//
// NOTE the generated client puts classId FIRST:
//   listRides(classId, showId, day, ring, needsReview, riderId, horseId)
// so a ring query is listRides(undefined, showId, undefined, ring).
//
// `nonce` is the slow entity-refresh beat: bump it to force a revalidate (a
// scratch adds/removes rows, and only a re-fetch will show it).
//
// #2190 — no local cache: a network failure leaves whatever rides are
// already on screen exactly as they are (see useRemoteResource).
// #2217 — `[showId, ring]` as `resourceKey` (docs in useRemoteResource.js
// and useRingStateForRing.js): a ring switch resets this hook's `data`
// back to the fresh-mount state instead of leaving the OLD ring's rides on
// screen (and `rows.length > 0`) through the new ring's in-flight window,
// same reasoning as ring-state — AC3 applies it consistently.
//
// #2227: deliberately keeps `initialValue` at useRemoteResource's default
// (`null`), unlike useRingStateForRing's `undefined`. A failed FIRST fetch
// after a resourceKey reset settles `data` to `null`, OrderOfGo's
// `filteredRides` reads that as `rides || []`, and `rows.length === 0`
// renders `oog-empty` ("No rides scheduled for this ring."). That is the
// SAME cold-start-failure-reads-as-empty contract useRemoteResource has
// documented since #2190, for every caller including this one, before
// #2217 or #2227 existed: a first mount whose fetch failed already showed
// `oog-empty`, no different in kind from what a resourceKey reset now also
// reaches. Unlike ring-state, `null`/`[]` here were never given a second,
// stronger meaning the way `resolveRingTruth` reads ring-state's `null` as
// "server confirmed no live class" (rule 8): "no rides" is this hook's own
// designed empty answer, not a promoted absence-of-data. So #2217's reset
// only makes an already-accepted fallback reachable one animation frame
// earlier (after a switch, not just a mount), it does not create a new
// class of false claim, which is why this hook is not changed to match
// ring-state's `undefined` treatment (card #2227 AC2).
export function useRidesForRing(showId, ring, nonce = 0) {
  const api = useScoreApi();
  return useRemoteResource(
    async () => {
      if (!showId || !ring) return [];
      return unwrap(await api.rides.listRides(undefined, showId, undefined, ring)) ?? [];
    },
    [api, showId, ring, nonce],
    [showId, ring],
  );
}

export default useRidesForRing;
